import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cable,
  Network,
  Satellite,
  Zap,
  ShieldCheck,
  Award,
  Handshake,
  Leaf,
  MapPin,
  Check,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/lib/company";

const serviceIcons = {
  fiber: Cable,
  network: Network,
  broadband: Satellite,
  electro: Zap,
};
const valueIcons = [ShieldCheck, Award, Handshake, Leaf];

export default function Home() {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const services = lang === "de" && content?.services ? content.services : t.services;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5] bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-28">
          <div>
            <Reveal>
              <Overline>{t.hero.overline}</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-[#0A0A0A] sm:text-5xl lg:text-6xl">
                {t.hero.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A4A4A] md:text-lg">
                {t.hero.subtitle}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  data-testid="hero-primary-cta"
                  className="group flex items-center gap-2 bg-[#E8620E] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C4520C]"
                >
                  {t.hero.primaryCta}
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/leistungen"
                  data-testid="hero-secondary-cta"
                  className="border border-[#0A0A0A] px-7 py-3.5 text-sm font-semibold text-[#0A0A0A] transition-colors duration-200 hover:bg-[#0A0A0A] hover:text-white"
                >
                  {t.hero.secondaryCta}
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[#E5E5E5]">
                <img
                  src={IMAGES.networkHero}
                  alt="Netzwerk Infrastruktur"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 hidden bg-[#0A0A0A] px-6 py-4 sm:block">
                <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                  {t.hero.tagline}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-[#E5E5E5] px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-10">
          {t.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="py-10 sm:px-8">
              <div className="text-4xl font-bold tracking-tight text-[#0A0A0A] md:text-5xl">
                {s.value}
              </div>
              <div className="mt-2 text-sm text-[#757575]">{s.labelKey}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
          <Reveal>
            <Overline>{t.intro.overline}</Overline>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              {t.intro.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-[#4A4A4A] md:text-lg">
              {t.intro.text}
            </p>
            <Link
              to="/ueber-uns"
              data-testid="intro-about-link"
              className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#E8620E]"
            >
              {t.intro.cta}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="border-y border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <Overline>{t.servicesSection.overline}</Overline>
            <h2 className="mt-5 max-w-2xl text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              {t.servicesSection.title}
            </h2>
            <p className="mt-4 max-w-2xl text-base text-[#4A4A4A] md:text-lg">
              {t.servicesSection.subtitle}
            </p>
          </Reveal>

          <div className="mt-14 grid gap-px border border-[#E5E5E5] bg-[#E5E5E5] sm:grid-cols-2">
            {services.map((s, i) => {
              const Icon = serviceIcons[s.key];
              return (
                <Reveal key={s.key} delay={i * 0.06}>
                  <div className="group h-full bg-white p-8 transition-colors duration-200 hover:bg-[#0A0A0A] lg:p-10">
                    <Icon size={32} className="text-[#E8620E]" strokeWidth={1.5} />
                    <h3 className="mt-6 text-xl font-medium tracking-tight text-[#0A0A0A] transition-colors group-hover:text-white md:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A] transition-colors group-hover:text-white/70">
                      {s.short}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <Link
              to="/leistungen"
              data-testid="services-view-all"
              className="group mt-12 inline-flex items-center gap-2 border border-[#0A0A0A] px-7 py-3.5 text-sm font-semibold text-[#0A0A0A] transition-colors duration-200 hover:bg-[#0A0A0A] hover:text-white"
            >
              {t.servicesSection.viewAll}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <Overline>{t.valuesSection.overline}</Overline>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              {t.valuesSection.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {t.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="border-t-2 border-[#0A0A0A] pt-6">
                    <Icon size={28} className="text-[#E8620E]" strokeWidth={1.5} />
                    <h3 className="mt-5 text-lg font-medium text-[#0A0A0A]">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regions */}
      <section className="border-y border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-24">
          <Reveal>
            <Overline>{t.regions.overline}</Overline>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              {t.regions.title}
            </h2>
            <p className="mt-5 max-w-lg text-base text-[#4A4A4A] md:text-lg">
              {t.regions.text}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid gap-px border border-[#E5E5E5] bg-[#E5E5E5]">
              {t.regions.list.map((r) => (
                <div key={r} className="flex items-center gap-4 bg-white px-6 py-5">
                  <MapPin size={20} className="text-[#E8620E]" />
                  <span className="text-base font-medium text-[#0A0A0A]">{r}</span>
                  <Check size={18} className="ml-auto text-[#0A0A0A]" />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#0A0A0A]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10 lg:py-20">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">
              {t.ctaBand.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-white/70 md:text-lg">
              {t.ctaBand.text}
            </p>
          </div>
          <Link
            to="/kontakt"
            data-testid="cta-band-button"
            className="group flex shrink-0 items-center gap-2 bg-[#E8620E] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C4520C]"
          >
            {t.ctaBand.button}
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
